import React, {
	useState,
	useEffect,
	useMemo,
	useRef,
	useCallback,
} from "react";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../store/slices/authSlice";
import {
	useGetAllClassLogsQuery,
	useDeleteClassLogMutation,
} from "../../store/api/classLogApiSlice";
import {
	useGetUserDepartmentsQuery,
	useGetUserSubjectsQuery,
	useGetCurrentTeacherSubstituteSubjectQuery,
} from "../../store/api/userApiSlice";
import { useGetTeachersQuery } from "../../store/api/teacherApiSlice";
import { useWeekNavigation, useLogsFilter } from "./hooks";
import { Loading } from "../../components/common";
import { PageContainer } from "../../components/layout/";
import {
	ClassLogFormModal,
	LogDetailsModal,
	EditLogModal,
} from "../../components/modals";
import { LogsHeader, TimetableGrid, ErrorState } from "./components";
import styles from "./LogsPage.module.css";

const LogsPage = () => {
	const user = useSelector(selectCurrentUser);

	// RTK Query hooks
	const {
		data: classLogs = [],
		isLoading: logsLoading,
		error: logsError,
		refetch: refetchLogs,
	} = useGetAllClassLogsQuery();

	const { data: departments = [], isLoading: depsLoading } =
		useGetUserDepartmentsQuery();

	const { data: subjects = [], isLoading: subjectsLoading } =
		useGetUserSubjectsQuery();

	const { data: teachers = [], isLoading: teachersLoading } =
		useGetTeachersQuery();

	// NEW: Add substitute subject query
	const { data: substituteData, isLoading: substituteLoading } =
		useGetCurrentTeacherSubstituteSubjectQuery();

	const [deleteClassLog] = useDeleteClassLogMutation();

	// NEW: Add substitute mode state
	const [isSubstituteMode, setIsSubstituteMode] = useState(false);

	// Custom hooks
	const {
		weekdays,
		weekOffset,
		navigateToPreviousWeek,
		navigateToNextWeek,
		navigateToCurrentWeek,
		formatWeekRange,
		isCurrentWeek,
		canNavigateNext,
	} = useWeekNavigation();

	// State for modals with timestamps for refresh triggers
	const [modals, setModals] = useState({
		addLog: { isOpen: false, data: null, timestamp: 0 },
		viewDetails: { isOpen: false, data: null, timestamp: 0 },
		editLog: { isOpen: false, data: null, timestamp: 0 },
	});

	// Modal management functions
	const openModal = useCallback((modalName, data = null) => {
		setModals((prev) => ({
			...prev,
			[modalName]: {
				isOpen: true,
				data,
				timestamp: Date.now(),
			},
		}));
	}, []);

	const closeModal = useCallback((modalName) => {
		setModals((prev) => ({
			...prev,
			[modalName]: { isOpen: false, data: null, timestamp: 0 },
		}));
	}, []);

	const closeAllModals = useCallback(() => {
		setModals({
			addLog: { isOpen: false, data: null, timestamp: 0 },
			viewDetails: { isOpen: false, data: null, timestamp: 0 },
			editLog: { isOpen: false, data: null, timestamp: 0 },
		});
	}, []);

	// State
	const [selectedDepartment, setSelectedDepartment] = useState("");
	const [selectedDayIndex, setSelectedDayIndex] = useState(0);
	const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
	const initialDaySet = useRef(false);

	// Logs filtering
	const logsFilter = useLogsFilter(classLogs, selectedDepartment);

	// UPDATED: Filter departments based on substitute mode
	const filteredDepartments = useMemo(() => {
		if (!departments) return [];
		if (user?.role === "Admin") return departments;

		if (isSubstituteMode && substituteData) {
			// In substitute mode, return all departments from substitute data
			return substituteData.departmentDetails || [];
		}

		// Normal mode - return only teacher's assigned departments
		const teacherDeptIds = new Set();
		if (user?.assignedSubjects?.length > 0) {
			user.assignedSubjects.forEach((as) => {
				as.departmentId.forEach((depId) => teacherDeptIds.add(depId));
			});
		}
		return departments.filter((dept) => teacherDeptIds.has(dept.id));
	}, [departments, user, isSubstituteMode, substituteData]);

	// NEW: Get appropriate subjects for modals based on mode
	const modalSubjects = useMemo(() => {
		if (isSubstituteMode && substituteData) {
			// In substitute mode, only return the substitute subject
			return [
				{
					id: substituteData.subjectId,
					name: substituteData.subjectName,
				},
			];
		}
		// Normal mode - return all teacher's subjects
		return subjects;
	}, [isSubstituteMode, substituteData, subjects]);

	// Effects
	useEffect(() => {
		const handleResize = () => setIsMobile(window.innerWidth <= 768);
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		if (!selectedDepartment && filteredDepartments.length > 0) {
			setSelectedDepartment(filteredDepartments[0].id);
		}
	}, [filteredDepartments, selectedDepartment]);

	useEffect(() => {
		if (initialDaySet.current) return;
		const todayIndex = weekdays.findIndex((day) => day.isToday);
		if (todayIndex !== -1) {
			setSelectedDayIndex(todayIndex);
			initialDaySet.current = true;
		}
	}, [weekdays]);

	const handleSubstituteModeToggle = useCallback((newMode) => {
		setIsSubstituteMode(newMode);
		setSelectedDepartment("");
	}, []);

	// Memoized handlers to prevent re-renders
	const handleCellClick = useCallback(
		(action, data) => {
			openModal(action, data);
		},
		[openModal]
	);

	const handleEditLog = useCallback(
		(log) => {
			const editableLog = {
				ClassLog: log.classLog || log,
				absentStudents: log.absentStudents || [],
				teacherName: log.teacherName,
				departmentName: log.departmentName,
				subjectName: log.subjectName,
			};

			closeModal("viewDetails");
			openModal("editLog", editableLog);
		},
		[closeModal, openModal]
	);

	const handleDeleteLog = useCallback(
		async (logId) => {
			if (!logId) {
				console.error("Cannot delete log: missing ID");
				return;
			}

			try {
				await deleteClassLog(logId).unwrap();
				closeModal("viewDetails");
				refetchLogs();
			} catch (error) {
				console.error("Error deleting log:", error);
				alert("Failed to delete log. Please try again.");
			}
		},
		[deleteClassLog, closeModal, refetchLogs]
	);

	const handleLogSuccess = useCallback(() => {
		closeAllModals();
		refetchLogs();
	}, [closeAllModals, refetchLogs]);

	// Loading state - include substitute loading
	const isLoading =
		logsLoading ||
		depsLoading ||
		teachersLoading ||
		subjectsLoading ||
		substituteLoading;

	if (isLoading) {
		return (
			<PageContainer>
				<Loading message="Loading class logs..." />
			</PageContainer>
		);
	}

	// Error state
	if (logsError) {
		return (
			<PageContainer>
				<ErrorState error={logsError} onRetry={refetchLogs} />
			</PageContainer>
		);
	}

	return (
		<PageContainer className={styles.logsPage}>
			<LogsHeader
				weekRange={formatWeekRange()}
				isCurrentWeek={isCurrentWeek}
				canNavigateNext={canNavigateNext}
				onPreviousWeek={navigateToPreviousWeek}
				onNextWeek={navigateToNextWeek}
				onCurrentWeek={navigateToCurrentWeek}
				departments={filteredDepartments}
				selectedDepartment={selectedDepartment}
				onDepartmentChange={setSelectedDepartment}
				isMobile={isMobile}
				weekdays={weekdays}
				selectedDayIndex={selectedDayIndex}
				onDayChange={setSelectedDayIndex}
				// NEW: Add substitute mode props
				isSubstituteMode={isSubstituteMode}
				onSubstituteModeToggle={handleSubstituteModeToggle}
			/>

			<TimetableGrid
				weekdays={weekdays}
				isMobile={isMobile}
				selectedDayIndex={selectedDayIndex}
				logs={logsFilter}
				teachers={teachers}
				onCellClick={handleCellClick}
			/>

			{/* Add Log Modal - Updated to use modalSubjects */}
			{modals.addLog.isOpen && (
				<ClassLogFormModal
					isOpen={modals.addLog.isOpen}
					onClose={() => closeModal("addLog")}
					departmentId={selectedDepartment}
					disableDayAndPeriodSelection={true}
					externalDate={modals.addLog.data?.date}
					externalPeriod={modals.addLog.data?.period?.toString()}
					subjects={modalSubjects}
					onSuccess={handleLogSuccess}
					// NEW: Pass substitute mode info
					substituteMode={isSubstituteMode}
					substituteSubjectId={
						isSubstituteMode ? substituteData?.subjectId : null
					}
				/>
			)}

			{/* View Details Modal */}
			{modals.viewDetails.isOpen && (
				<LogDetailsModal
					isOpen={modals.viewDetails.isOpen}
					onClose={() => closeModal("viewDetails")}
					log={modals.viewDetails.data}
					logId={modals.viewDetails.data?.id}
					onEdit={handleEditLog}
					onDelete={handleDeleteLog}
					openTimestamp={modals.viewDetails.timestamp}
				/>
			)}

			{/* Edit Log Modal */}
			{modals.editLog.isOpen && (
				<EditLogModal
					isOpen={modals.editLog.isOpen}
					onClose={() => closeModal("editLog")}
					log={modals.editLog.data}
					onUpdateSuccess={handleLogSuccess}
				/>
			)}
		</PageContainer>
	);
};

export default LogsPage;
